<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { Address } from '@/models/Address';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import { type AddressInputStatus } from '@/components/session/kyc/address/address.types';
import { useDebounceFn, whenever } from '@vueuse/core';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

interface GmpSelectEvent extends Event {
  placePrediction: google.maps.places.PlacePrediction;
}

const addressInputStatus: Ref<AddressInputStatus> = ref('Input');
const model = defineModel({
  type: Address,
  required: true,
});

const props = defineProps({
  regionOverride: {
    type: String,
    required: true,
  },
  switchMode: {
    type: Function,
    required: true,
  },
  saveAddress: {
    type: Function,
    required: true,
  },
});

const buttonText = computed(() => (addressInputStatus.value === 'Checking' ? 'Checking' : 'Next'));

const isNextEnabled = ref(false);
const isValidationVisible = ref(false);
const addressInput = ref<(HTMLElement & { value?: string }) | null>(null);

const fullAddressString = model.value?.fullAddress;

whenever(
  () => isValidationVisible.value,
  () => Mixpanel.instance.trackAddressValidationFail()
);

const onFullAddressStringUpdate = useDebounceFn(async (newFullAddressString: string) => {
  if (newFullAddressString) {
    Mixpanel.instance.trackVerifiableAddressEntered();
  }
  await checkData(newFullAddressString);
}, 500);

async function checkData(newFullAddressString: string) {
  isValidationVisible.value = false;
  isNextEnabled.value = false;

  try {
    if (!newFullAddressString) {
      //handling on input change
      return;
    }

    addressInputStatus.value = 'Checking';

    const newAddress = await Address.tryParseAddressString(
      newFullAddressString,
      props.regionOverride
    );

    //address has all the parts and it is the same as the previous one
    if (!newAddress.missingParts && newAddress.address.fullAddress === model.value?.fullAddress) {
      model.value = newAddress.address;
      isNextEnabled.value = true;
      isValidationVisible.value = false;
      return;
    }

    const isMissingStreetNumber = newAddress.missingParts?.includes('street_number');
    const justMissingStreetNumber = newAddress.missingParts?.length === 1 && isMissingStreetNumber;

    //if it's just missing the street number we're good to model bind.
    if (!justMissingStreetNumber && !newAddress.hasAllParts) {
      isValidationVisible.value = true;
      return;
    }

    model.value = newAddress.address;
    if (addressInput.value) {
      addressInput.value.value = newAddress.address.fullAddress ?? '';
    }

    // we're going to update the model, but we're not saying this is a valid address.
    if (justMissingStreetNumber) {
      isValidationVisible.value = true;
      return;
    }
    isNextEnabled.value = true;
    isValidationVisible.value = false;
    Mixpanel.instance.trackAddressValidationSuccess();
  } finally {
    addressInputStatus.value = 'Saving';
  }
}

async function addressSelectionChanged(e: Event) {
  try {
    const event = e as GmpSelectEvent;
    const place = event.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress'],
    });
    if (place.formattedAddress) {
      await checkData(place.formattedAddress);
    }
  } catch (err) {
    Sentry.captureException(err);
  }
}

function onInput(e: Event) {
  isValidationVisible.value = false;
  isNextEnabled.value = false;
  const input = e.target as HTMLElement & { value?: string };
  if (input.value) {
    model.value = Address.create();
  }
}

async function onSaveAddress() {
  await props.saveAddress();
  return true;
}

onMounted(() => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const autocompleteOptions: any = {
      includedPrimaryTypes: ['street_address'],
      includedRegionCodes: [props.regionOverride],
    };

    const autocompleteElement = new google.maps.places.PlaceAutocompleteElement(
      autocompleteOptions
    );
    addressInput.value = autocompleteElement as HTMLElement & { value?: string };
    autocompleteElement.setAttribute('value', fullAddressString ?? '');
    autocompleteElement.id = 'place-autocomplete-input';

    const card = document.getElementById('place-autocomplete-card');
    if (card) {
      card.appendChild(autocompleteElement);
    }

    autocompleteElement.addEventListener('gmp-select', addressSelectionChanged);
    autocompleteElement.addEventListener('input', onInput);
    autocompleteElement.addEventListener('blur', async (e: Event) => {
      const input = e.target as HTMLElement & { value?: string };
      if (input.value) {
        try {
          await onFullAddressStringUpdate(input.value);
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    });
  } catch (e) {
    Sentry.captureException(e);
  }
});

async function init() {
  await checkData(fullAddressString ?? '');
}

init();
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col">
      <h1 class="text-3xl">What is your current residential address?</h1>
      <div class="mt-4 mb-4">Your address cannot be a PO box</div>
      <div>
        <div class="flex flex-1 flex-col" data-test="screen-address-auto">
          <label for="place-autocomplete-input" class="text-light-primary mb-2 font-bold"
            >Residential address</label
          >
          <div id="place-autocomplete-card"></div>
        </div>
      </div>
      <p
        class="text-light-utility-red dark:text-dark-utility-red mt-1 py-0"
        v-if="isValidationVisible"
        data-test="address-validation-error"
      >
        <span
          >We could not validate this address. Please check the address and try again or enter the
          address manually.</span
        >
      </p>
      <a
        href="#"
        class="text-light-secondary dark:text-dark-border mt-2 mb-16 text-sm font-semibold underline"
        @click.prevent="props.switchMode()"
        data-test="change-mode"
      >
        <span>Enter address manually</span>
      </a>
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton
        :preNavigationAction="onSaveAddress"
        :text="buttonText"
        :disabled="!isNextEnabled"
        direction="forward"
      />
    </template>
  </KycSessionLayout>
</template>

<style>
@reference "../../../../style.css";
@reference "tailwindcss";

#place-autocomplete-input {
  @apply border-light-border text-light-primary w-full rounded-md border bg-transparent;
  color-scheme: light;
}
</style>
